import React, { useMemo, useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Stack,
  Box,
} from "@mui/material";
import { useConsent } from "../consent/ConsentContext";

const DEFAULT = { necessary: true, analytics: false };
const ALL_ON  = { necessary: true, analytics: true };

export default function CookieBanner() {
  const {
    consent,
    setConsent,
    preferencesOpen,
    openPreferences,
    closePreferences,
  } = useConsent();

  const showBanner = consent === null;

  const current = useMemo(() => consent ?? DEFAULT, [consent]);
  const [draft, setDraft] = useState(DEFAULT);

  useEffect(() => {
  if (!preferencesOpen) return;

  // ✅ wenn noch keine Entscheidung: beim Öffnen alles angehakt
  if (consent === null) {
    setDraft(ALL_ON);
  } else {
    // ✅ sonst: gespeicherten Consent ins Draft übernehmen
    setDraft(consent);
  }
}, [preferencesOpen, consent]);


  const acceptAll = () => {
    setConsent(ALL_ON);
    closePreferences();
  };

  const acceptNecessaryOnly = () => {
    setConsent({ necessary: true, analytics: false });
    closePreferences();
  };

  // ✅ beim ersten Öffnen: alles „angehakt“ (also analytics=true)
const handleOpenPreferences = () => {
  openPreferences();
};

  const save = () => {
    setConsent({ necessary: true, analytics: !!draft.analytics });
    closePreferences();
  };

  return (
    <>
      {showBanner && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            left: { xs: 10, sm: 16 },
            right: { xs: 10, sm: 16 },
            bottom: { xs: 10, sm: 16 },
            p: { xs: 2.4, sm: 3.0 }, // ✅ mehr Höhe
            borderRadius: 2,
            zIndex: 2000,
            // Landingpage-Look: dunkles Night-Panel mit Lime-Akzent
            bgcolor: "#16180f",
            color: "#f2f1ea",
            border: "1px solid rgba(242,241,234,0.13)",
          }}
          role="dialog"
          aria-label="Cookie Einwilligung"
        >
          <Typography sx={{ fontWeight: 950, mb: 0.8, fontSize: { xs: 18, sm: 18 } }}>
            Cookies & Tracking
          </Typography>

          <Typography
            sx={{
              color: "rgba(242,241,234,0.72)",
              mb: 2.0,
              lineHeight: 1.65,
              fontSize: { xs: 14.5, sm: 15 },
            }}
          >
Wir verwenden Cookies und ein Analyse-Tool, um die Funktionalität unserer Website zu gewährleisten und die Nutzung zu verbessern.          </Typography>

          {/* ✅ Buttons links + mobile: untereinander und full width */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignItems: "flex-start" }}>
            <Button
              variant="outlined"
              onClick={handleOpenPreferences}
              sx={{
                width: { xs: "100%", sm: "auto" },
                justifyContent: "flex-start",
                color: "#f2f1ea",
                borderColor: "rgba(242,241,234,0.35)",
                "&:hover": { borderColor: "#f2f1ea", background: "rgba(242,241,234,0.08)" },
              }}
            >
              Anpassen
            </Button>

            <Button
              variant="contained"
              onClick={acceptAll}
              sx={{
                width: { xs: "100%", sm: "auto" },
                justifyContent: "flex-start",
                bgcolor: "#c3f04c",
                color: "#14150c",
                fontWeight: 700,
                "&:hover": { bgcolor: "#b5e63e" },
              }}
            >
              Akzeptieren
            </Button>
          </Stack>
        </Paper>
      )}

      {/* ✅ Dialog kann später über Footer wieder geöffnet werden */}
      <Dialog open={preferencesOpen} onClose={closePreferences} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Cookie-Einstellungen</DialogTitle>

        <DialogContent dividers>
          <FormControlLabel control={<Switch checked disabled />} label="Notwendig (immer aktiv)" />

          <FormControlLabel
            control={
              <Switch
                checked={!!draft.analytics}
                onChange={(e) => setDraft((d) => ({ ...d, analytics: e.target.checked }))}
              />
            }
            label="Analytics (PostHog)"
          />

          <Typography sx={{ mt: 2, color: "text.secondary", fontSize: 13, lineHeight: 1.55 }}>
            Du kannst das später jederzeit ändern.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, justifyContent: "flex-start" }}>
          <Button onClick={acceptNecessaryOnly} variant="outlined">
            Nur notwendige
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={save} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
